import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.addEventListener('click', this.#preventNativeToggle);
    this.#setupAnimatedElementListeners();
    this.#observeAnnouncementHeight();
  }

  // Owner correction (2026-09-02): the post-load "reopen the drawer after a language switch"
  // correction that used to live here is removed - it necessarily painted the drawer closed
  // first and corrected it after window 'load', a visible flash. The same sessionStorage flag
  // (still set by assets/localization.js#changeLanguage) is now consumed by an early inline
  // <script> in snippets/header-drawer.liquid itself, which runs before this component (or any
  // other deferred script) loads, so the drawer is already open at first paint - see that
  // script's own comment for the full reasoning.

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.removeEventListener('click', this.#preventNativeToggle);
    this.#announcementResizeObserver?.disconnect();
  }

  /** @type {ResizeObserver | undefined} */
  #announcementResizeObserver;

  /** Element to restore focus to when the top-level drawer closes. */
  /** @type {HTMLElement | null} */
  #previouslyFocused = null;

  /**
   * Guards the top-level drawer's close animation against re-entrancy.
   * Mirrors assets/theme-drawer.js's #isClosing/#deferredOpen (ThemeDrawer,
   * used by Cart) - this component never had an equivalent, so a close()
   * call arriving while a previous close() was still mid-animation (e.g.
   * the Menu trigger doubling as open/close, clicked again before the
   * close transition finished) started a SECOND, overlapping
   * requestAnimationFrame -> onAnimationEnd -> reset()/dispatch chain on
   * top of the first. Root-caused (2026-08-11) as the source of an
   * intermittent freeze: assets/header.js's #activeOverlay coordinator
   * (added 2026-08-09, commit 7d2891b) correlates open/close purely by a
   * single mutable string and clears/unlocks scroll only when it sees
   * THIS drawer's close event - a stale, second dispatch from an
   * overlapping chain (or one that resolves out of order, since each
   * chain's onAnimationEnd() re-queries getAnimations() independently)
   * could fire after a later reopen and incorrectly tear down that
   * session's state, permanently stranding the shared scroll-lock owners
   * Set (assets/utilities.js) since this element never disconnects.
   * Scoped to the top-level drawer only (this.refs.details) - submenu
   * open/close (open() with a target, back()) is untouched.
   * @type {boolean}
   */
  #isClosing = false;

  /**
   * When set, the top-level drawer reopens with these arguments once the
   * in-progress close finishes, instead of opening immediately mid-close.
   * @type {{ event?: Event, options?: object } | false}
   */
  #deferredOpen = false;

  /**
   * Measures the announcement bar's own rendered height directly (not
   * derived from --header-group-height, which sums every direct child of
   * #header-group — including .header-section, a different DOM node from
   * header-component — and so does not equal the announcement bar alone).
   * Sets --mobile-announcement-height on body, the shared ancestor this
   * component's own top/height formulas and sections/header.liquid's
   * header offset both read from. Unconditional on connect: not gated on
   * drawer open/closed state, so closed and open share identical geometry.
   * Becomes 0px if the announcement bar is absent from the page.
   */
  #observeAnnouncementHeight() {
    const announcementBar = document.querySelector('.announcement-bar');

    if (!(announcementBar instanceof HTMLElement)) {
      document.body.style.setProperty('--mobile-announcement-height', '0px');
      return;
    }

    this.#announcementResizeObserver = new ResizeObserver((entries) => {
      const height = entries[0]?.borderBoxSize[0]?.blockSize ?? announcementBar.offsetHeight;
      document.body.style.setProperty('--mobile-announcement-height', `${Math.round(height)}px`);
    });
    this.#announcementResizeObserver.observe(announcementBar);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    const details = this.#getDetailsElement(event);

    // Route the top-level drawer through the same guarded close() as the
    // trigger click, so Escape participates in the same re-entrancy
    // protection (#isClosing/#deferredOpen, above) instead of bypassing
    // it via a direct #close() call. Submenu closes are unaffected.
    if (details === this.refs.details) {
      this.close();
    } else {
      this.#close(details);
    }
  };

  /**
   * Clicking a <summary> also triggers the browser's native <details>
   * toggle as a default action, which flips the `open` attribute
   * synchronously and, in browsers with the newer interactive <details>
   * behavior, hides its content via `content-visibility` immediately —
   * well before our own CSS transition (gated behind #close()'s
   * animation-end handling below) has had any chance to play. That race,
   * not our transition/timing values, is why closing looked instant/
   * unanimated regardless of what duration or easing was set.
   * Preventing the default action here hands `open`'s timing entirely to
   * open()/#close() below. The existing on:click bindings on each
   * summary still run normally and still drive open()/close() — this
   * listener only cancels the native side effect, it doesn't replace
   * that call.
   *
   * Owner correction (2026-08-13): this used to be scoped to the
   * top-level summary only, on the assumption that nested submenu
   * summaries (localization, and a new Customer Care submenu built on
   * the same pattern) were "unaffected" since they close via a dedicated
   * Back button rather than a native toggle. That's true for closing,
   * but not for the very first OPEN click: the delegated on:click
   * handler (assets/component.js, capture-phase) runs open() — which
   * sets the `open` attribute via JS — BEFORE the browser's own default
   * action for this same click executes (default actions always run
   * last). Since open() already made it "open" by the time the native
   * toggle checks current state, the native toggle then flips it straight
   * back to closed, in the same click - the submenu opened and closed
   * before ever painting, which is exactly the reported "clicking does
   * nothing" symptom. Now applies to every summary in this component,
   * not just the top-level one - every summary here is JS-driven via
   * open()/back(), so none of them need the native default action.
   *
   * Owner correction (2026-08-14): excludes summaries inside
   * <accordion-custom> (the Customer Care order/payment/shipping/
   * return/size/product/organic compliance/accessibility/other/
   * assistance rows, snippets/header-drawer.liquid - now wrapped in
   * accordion-custom-component.liquid, the same shared accordion
   * mechanism the product page uses, so its own smooth open/close
   * animation applies here too). That component is the inverse of
   * every other summary here: it relies entirely on the browser's own
   * native <details> toggle, with no open()/back() equivalent driving
   * it (its own click handler, assets/accordion-custom.js, only ever
   * calls preventDefault() for its own disable-on-mobile/desktop
   * flags, neither set here). Blocking the native default for it the
   * same way as the rest of this component would silently break its
   * open/close with nothing else to fall back on.
   * @param {MouseEvent} event
   */
  #preventNativeToggle = (event) => {
    if (!(event.target instanceof Element)) return;

    const summary = event.target.closest('summary');
    if (summary && !summary.closest('accordion-custom')) {
      event.preventDefault();
    }
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   * @param {object} [options]
   * @param {HTMLElement} [options.restoreFocusTo] - Element to focus when the top-level drawer closes, instead of the current document.activeElement. Used by header-component's Menu/Cart overlay coordinator (assets/header.js) so switching between the two mid-session still restores focus to whatever triggered the very first one. Ignored for submenu opens (target is set).
   */
  open(target, event, options = {}) {
    if (!target && this.#isClosing) {
      this.#deferredOpen = { event, options };
      return;
    }

    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    // Set explicitly rather than relying on the native <details> default
    // action: for the top-level drawer that default action is now
    // prevented (see #preventNativeToggle), so nothing else will add this.
    // Harmless/idempotent for the localization submenu, whose native toggle
    // is untouched and would set this anyway.
    details.setAttribute('open', '');
    summary.setAttribute('aria-expanded', 'true');

    if (!target) {
      this.#previouslyFocused = options.restoreFocusTo ?? /** @type {HTMLElement | null} */ (document.activeElement);
      this.dispatchEvent(new HeaderDrawerOpenEvent());
    }

    this.preventInitialAccordionAnimations(details);

    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   * @param {object} [options]
   * @param {boolean} [options.skipFocusRestore] - See #close().
   */
  close(options = {}) {
    if (this.#isClosing) {
      // Already closing - cancel any deferred reopen so the drawer stays
      // closed once the in-progress animation finishes, mirroring
      // assets/theme-drawer.js's close().
      this.#deferredOpen = false;
      return;
    }
    this.#isClosing = true;
    this.#close(this.refs.details, options);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   * @param {object} [options]
   * @param {boolean} [options.skipFocusRestore] - When true, don't move focus back to the trigger. Used by header-component's Menu/Cart overlay coordinator (assets/header.js) when this close is really a switch to the other overlay — restoring focus here would yank it away from the overlay that's taking over a moment later. Only meaningful for the top-level drawer (details === this.refs.details); submenu closes never restore focus to a trigger to begin with.
   */
  #close(details, options = {}) {
    const { skipFocusRestore = false } = options;
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    // One frame between removing the class and reading getAnimations(),
    // mirroring open() above: querying in the same tick as the class change
    // can miss the transition the browser is only about to start, which
    // would resolve this immediately and strip `open` (see open()'s comment
    // on #preventNativeToggle) before the close transition has played.
    requestAnimationFrame(() => {
      onAnimationEnd(
        drawer || details,
        () => {
          reset(details);
          if (details === this.refs.details) {
            removeTrapFocus();
            const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
            openDetails.forEach(reset);

            this.dispatchEvent(new HeaderDrawerCloseEvent());

            if (!skipFocusRestore) {
              const trigger = this.#previouslyFocused;
              this.#previouslyFocused = null;
              if (trigger && document.contains(trigger)) {
                trigger.focus();
              }
            }

            this.#isClosing = false;

            if (this.#deferredOpen) {
              const { event: deferredEvent, options: deferredOptions } = this.#deferredOpen;
              this.#deferredOpen = false;
              this.open(undefined, deferredEvent, deferredOptions);
            }
          } else {
            trapFocus(this.refs.details);
          }
        },
        { subtree: false }
      );
    });
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Dispatched when the top-level menu drawer finishes opening. Mirrors
 * assets/theme-drawer.js's DrawerOpenEvent. Not dispatched for submenu
 * opens.
 */
export class HeaderDrawerOpenEvent extends CustomEvent {
  constructor() {
    super(HeaderDrawerOpenEvent.eventName, { bubbles: true });
  }

  static eventName = 'header-drawer:open';
}

/**
 * Dispatched when the top-level menu drawer finishes closing. Mirrors
 * assets/theme-drawer.js's DrawerCloseEvent - header-component's Menu/Cart
 * overlay coordinator (assets/header.js) listens for this (and its Cart
 * equivalent) to stay in sync when a drawer closes via a path other than
 * the coordinator itself (e.g. Escape). Not dispatched for submenu closes.
 */
export class HeaderDrawerCloseEvent extends CustomEvent {
  constructor() {
    super(HeaderDrawerCloseEvent.eventName, { bubbles: true });
  }

  static eventName = 'header-drawer:close';
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
